import { useEffect, useState } from "react";

/**
 * Global LogoLoader (Option A — placeholder spade-mark animation).
 *
 * Plays once per first visit (sessionStorage flag), holds for at least
 * `minMs`, then fades out. Pure CSS/SVG — zero network dependency.
 *
 * Hybrid handoff: when an admin uploads a logo loop video later, we'll
 * accept a `videoSrc` prop and play it instead of the SVG. Until then,
 * the SVG plays.
 *
 * Independent of any per-video loader.
 */
export default function LogoLoader({ videoSrc = "", minMs = 1000, sessionKey = "ap-loader-shown-v1" }) {
  const [visible, setVisible] = useState(true);
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    // First visit only — skip if already shown this session
    let alreadyShown = false;
    try {
      alreadyShown = sessionStorage.getItem(sessionKey) === "1";
    } catch {
      /* private mode — show anyway */
    }
    if (alreadyShown) {
      setVisible(false);
      return undefined;
    }

    // Ensure min display time, then fade
    const start = performance.now();
    const fade = () => {
      const elapsed = performance.now() - start;
      const wait = Math.max(0, minMs - elapsed);
      setTimeout(() => {
        setFadingOut(true);
        setTimeout(() => {
          setVisible(false);
          try {
            sessionStorage.setItem(sessionKey, "1");
          } catch {
            /* ignore */
          }
        }, 700); // fade duration
      }, wait);
    };

    if (document.readyState === "complete") {
      fade();
      return undefined;
    }
    const onReady = () => fade();
    window.addEventListener("load", onReady, { once: true });
    // Safety net — fade even if `load` never fires (long-lived async fetches)
    const safety = setTimeout(fade, minMs + 1500);
    return () => {
      window.removeEventListener("load", onReady);
      clearTimeout(safety);
    };
  }, [minMs, sessionKey]);

  if (!visible) return null;

  return (
    <div
      data-testid="global-loader"
      data-state={fadingOut ? "fading" : "visible"}
      aria-hidden="true"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black"
      style={{
        opacity: fadingOut ? 0 : 1,
        transition: "opacity 700ms cubic-bezier(0.4, 0, 0.2, 1)",
        pointerEvents: fadingOut ? "none" : "auto",
      }}
    >
      {/* Subtle vignette grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 ap-grain opacity-40 pointer-events-none"
      />

      {videoSrc ? (
        <video
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          className="w-40 h-40 md:w-56 md:h-56 object-contain"
        />
      ) : (
        <SpadeMark />
      )}
    </div>
  );
}

/* Animated spade — gold stroke draw + soft pulse — a clean placeholder
   that matches the brand mark style without depending on uploaded assets.
   Exported so per-video loaders (case study, blog) can reuse the exact
   same brand mark animation as the global page loader. */
export function SpadeMark({ size = "default", showCaption = true }) {
  const isCompact = size === "compact";
  return (
    <div
      className="relative flex flex-col items-center"
      style={{ animation: "ap-loader-pulse 2.4s ease-in-out infinite" }}
    >
      <svg
        width={isCompact ? "40" : "56"}
        height={isCompact ? "40" : "56"}
        viewBox="0 0 56 56"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={isCompact ? "md:w-12 md:h-12" : "md:w-16 md:h-16"}
      >
        <path
          d="M28 6
             C28 6, 14 18, 14 30
             A10 10 0 0 0 24 40
             C26 40, 27 39, 28 37
             C29 39, 30 40, 32 40
             A10 10 0 0 0 42 30
             C42 18, 28 6, 28 6 Z
             M28 36
             L24 50
             L32 50 Z"
          stroke="#C9A050"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          style={{
            strokeDasharray: 220,
            strokeDashoffset: 220,
            animation: "ap-loader-draw 1.8s cubic-bezier(0.65, 0, 0.35, 1) forwards",
          }}
        />
      </svg>
      {showCaption && (
        <p
          className={`font-display ${isCompact ? "text-[9px] tracking-[0.35em] mt-3" : "text-[11px] tracking-[0.4em] mt-5"} text-[#C9A050] uppercase`}
          style={{ animation: "ap-loader-fadein 1.4s ease 0.7s both" }}
        >
          Artistry · Perspectives
        </p>
      )}
      <style>{`
        @keyframes ap-loader-draw {
          to { stroke-dashoffset: 0; }
        }
        @keyframes ap-loader-fadein {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ap-loader-pulse {
          0%, 100% { opacity: 1;   transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(0.985); }
        }
      `}</style>
    </div>
  );
}
